<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  
  const dispatch = createEventDispatcher<{ win: number }>();
  const dispatchLoss = createEventDispatcher<void>();
  
  let { onBack, playerName = 'Player' } = $props();
  
  // Simple chess game state
  let board = $state(Array(64).fill(null));
  let selectedSquare = $state<number | null>(null);
  let currentPlayer = $state('white');
  let moveCount = $state(0);
  let gameMessage = $state('Your turn! White pieces.');
  let difficulty = $state(1); // 1-5, increases with wins
  
  // Initialize board
  function initBoard() {
    const initialPieces = [
      'r', 'n', 'b', 'q', 'k', 'b', 'n', 'r',
      'p', 'p', 'p', 'p', 'p', 'p', 'p', 'p',
      null, null, null, null, null, null, null, null,
      null, null, null, null, null, null, null, null,
      null, null, null, null, null, null, null, null,
      null, null, null, null, null, null, null, null,
      'P', 'P', 'P', 'P', 'P', 'P', 'P', 'P',
      'R', 'N', 'B', 'Q', 'K', 'B', 'N', 'R'
    ];
    board = [...initialPieces];
  }
  
  $effect(() => {
    initBoard();
  });
  
  const pieceSymbols: Record<string, string> = {
    'K': '♔', 'Q': '♕', 'R': '♖', 'B': '♗', 'N': '♘', 'P': '♙',
    'k': '♚', 'q': '♛', 'r': '♜', 'b': '♝', 'n': '♞', 'p': '♟'
  };
  
  function selectSquare(index: number) {
    if (selectedSquare === index) {
      selectedSquare = null;
      return;
    }
    
    if (selectedSquare !== null && board[selectedSquare]) {
      // Try to move
      movePiece(selectedSquare, index);
    } else if (board[index] && isWhitePiece(board[index])) {
      selectedSquare = index;
    }
  }
  
  function isWhitePiece(piece: string | null): boolean {
    return piece !== null && piece === piece.toUpperCase();
  }
  
  function movePiece(from: number, to: number) {
    const piece = board[from];
    const target = board[to];
    
    // Simple validation - can capture opponent or move to empty
    if (!target || !isWhitePiece(target)) {
      board[to] = piece;
      board[from] = null;
      selectedSquare = null;
      moveCount++;
      
      // Simulate AI move after delay
      setTimeout(() => aiMove(), 500);
      
      // Check for win condition (simplified)
      if (moveCount > 10 && Math.random() > 0.7) {
        handleWin();
      }
    }
  }
  
  function aiMove() {
    // Simple AI - random valid move
    const blackPieces = board.map((p, i) => ({ piece: p, index: i }))
      .filter(({ piece }) => piece && piece === piece.toLowerCase());
    
    if (blackPieces.length > 0) {
      const randomPiece = blackPieces[Math.floor(Math.random() * blackPieces.length)];
      const possibleMoves = getPossibleMoves(randomPiece.index);
      
      if (possibleMoves.length > 0) {
        const move = possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
        board[move] = board[randomPiece.index];
        board[randomPiece.index] = null;
        
        // Check for loss (king captured)
        if (!board.some(p => p === 'K')) {
          handleLoss();
        }
      }
    }
  }
  
  function getPossibleMoves(index: number): number[] {
    // Simplified move calculation
    const moves: number[] = [];
    const row = Math.floor(index / 8);
    const col = index % 8;
    
    // Add forward moves for pawns, etc.
    if (row < 7) moves.push(index + 8);
    if (row > 0) moves.push(index - 8);
    
    return moves.filter(i => i >= 0 && i < 64 && !board[i]);
  }
  
  function handleWin() {
    const points = 100 * difficulty;
    gameMessage = `Checkmate! You won ${points} points!`;
    dispatch('win', points);
    difficulty = Math.min(difficulty + 1, 5);
  }
  
  function handleLoss() {
    gameMessage = 'Game Over! Your king was captured.';
    dispatchLoss();
  }
  
  function resetGame() {
    initBoard();
    selectedSquare = null;
    currentPlayer = 'white';
    moveCount = 0;
    gameMessage = 'New game! Your turn.';
  }
</script>

<div class="chess-game">
  <div class="game-header">
    <button class="back-btn" onclick={onBack}>← Back</button>
    <h2>♟️ 3D Chess - Level {difficulty}</h2>
    <div class="player-info">{playerName} vs AI</div>
  </div>
  
  <div class="game-message">{gameMessage}</div>
  
  <div class="chess-board">
    {#each board as piece, index}
      <div 
        class="square {(Math.floor(index / 8) + index % 8) % 2 === 0 ? 'light' : 'dark'} {selectedSquare === index ? 'selected' : ''}"
        onclick={() => selectSquare(index)}
      >
        {#if piece}
          <span class="piece">{pieceSymbols[piece]}</span>
        {/if}
      </div>
    {/each}
  </div>
  
  <div class="game-controls">
    <button class="action-btn" onclick={resetGame}>🔄 New Game</button>
    <button class="action-btn" onclick={() => gameMessage = 'Thinking...'}>💡 Hint</button>
  </div>
  
  <div class="difficulty-indicator">
    <span>Difficulty:</span>
    <div class="level-dots">
      {#each Array(5) as _, i}
        <span class="dot {i < difficulty ? 'active' : ''}"></span>
      {/each}
    </div>
  </div>
</div>

<style>
  .chess-game {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
  }
  
  .game-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 15px;
  }
  
  .back-btn, .action-btn {
    background: linear-gradient(135deg, #2ecc71, #27ae60);
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 10px;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
  }
  
  .back-btn:hover, .action-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(46, 204, 113, 0.4);
  }
  
  h2 {
    color: #2d5016;
    font-size: 1.5rem;
  }
  
  .player-info {
    color: #5a7a5a;
    font-weight: 600;
  }
  
  .game-message {
    text-align: center;
    padding: 15px;
    background: linear-gradient(135deg, #f0fff4, #d4edda);
    border-radius: 15px;
    margin-bottom: 20px;
    font-weight: 600;
    color: #2d5016;
  }
  
  .chess-board {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    gap: 2px;
    border: 4px solid #2d5016;
    border-radius: 10px;
    overflow: hidden;
    aspect-ratio: 1;
  }
  
  .square {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: clamp(1.5rem, 5vw, 2.5rem);
    cursor: pointer;
    transition: all 0.2s ease;
  }
  
  .square.light {
    background: #f0fff4;
  }
  
  .square.dark {
    background: #27ae60;
  }
  
  .square.selected {
    background: #f1c40f !important;
    box-shadow: inset 0 0 10px rgba(0,0,0,0.3);
  }
  
  .square:hover {
    filter: brightness(1.1);
  }
  
  .piece {
    user-select: none;
    filter: drop-shadow(2px 2px 2px rgba(0,0,0,0.3));
  }
  
  .game-controls {
    display: flex;
    gap: 15px;
    justify-content: center;
    margin-top: 20px;
  }
  
  .difficulty-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    margin-top: 20px;
    color: #2d5016;
  }
  
  .level-dots {
    display: flex;
    gap: 5px;
  }
  
  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #e0e0e0;
  }
  
  .dot.active {
    background: #2ecc71;
    box-shadow: 0 0 10px #2ecc71;
  }
  
  @media (max-width: 500px) {
    .chess-board {
      gap: 1px;
    }
    
    .square {
      font-size: 1.5rem;
    }
  }
</style>
